import { useEffect, useState } from 'react';
import {
  JORNAYA_DISCLOSURE_NODE_ID,
  JORNAYA_TOKEN_WAIT_MS,
  readJornayaToken,
} from './leadComplianceTokens';

const JORNAYA_SRC_MATCH =
  'script[src*="create.lidstatic.com/campaign/1a8b1fee-893f-e8f5-901a-5d12b2e0d47b.js"]';
const JORNAYA_SRC =
  '//create.lidstatic.com/campaign/1a8b1fee-893f-e8f5-901a-5d12b2e0d47b.js?snippet_version=2';
const POLL_MS = 100;

export { readJornayaToken };

/**
 * Inserts the campaign script after React has committed the disclosure node.
 * The script snapshots that node when it runs, then requests a LeadID.
 * A tag that is already in the document is left alone: while it is
 * downloading, `window.LeadiD` is still unset, and removing it would start
 * a second copy. A load error retries once.
 */
function installCampaignScript(): void {
  if (document.querySelector(JORNAYA_SRC_MATCH)) return;
  appendCampaignScript(false);
}

function appendCampaignScript(retried: boolean): void {
  const script = document.createElement('script');
  script.id = 'LeadiDscript_campaign';
  script.type = 'text/javascript';
  script.async = true;
  script.src = JORNAYA_SRC;
  script.addEventListener('error', () => {
    script.parentNode?.removeChild(script);
    if (retried) return;
    appendCampaignScript(true);
  });
  document.body.appendChild(script);
}

/**
 * Injects the Jornaya LeadiD script once `enabled` is true and
 * `#leadid_tcpa_disclosure` is in the document. The script binds the TCPA
 * disclosure at execution time, so inserting it earlier mints a token with
 * no disclosure. Disabling the hook later does not remove the script. The
 * returned token updates when `window.LeadiD.token` appears.
 */
const useJornayaScript = (enabled: boolean): string => {
  const [token, setToken] = useState(readJornayaToken);
  const [disclosureReady, setDisclosureReady] = useState(false);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    let observer: MutationObserver | undefined;

    const markReady = () => {
      if (!document.getElementById(JORNAYA_DISCLOSURE_NODE_ID)) return;
      setDisclosureReady(true);
      observer?.disconnect();
      observer = undefined;
    };

    markReady();
    if (!document.getElementById(JORNAYA_DISCLOSURE_NODE_ID)) {
      observer = new MutationObserver(markReady);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer?.disconnect();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !disclosureReady || typeof document === 'undefined') return;

    installCampaignScript();

    const current = readJornayaToken();
    if (current) {
      setToken(current);
      return;
    }

    let pollTimer: ReturnType<typeof setInterval> | undefined;
    const stopPolling = () => {
      if (!pollTimer) return;
      clearInterval(pollTimer);
      pollTimer = undefined;
    };

    const started = Date.now();
    pollTimer = setInterval(() => {
      const next = readJornayaToken();
      if (next) {
        setToken(next);
        stopPolling();
        return;
      }
      if (Date.now() - started >= JORNAYA_TOKEN_WAIT_MS) {
        stopPolling();
      }
    }, POLL_MS);

    return () => stopPolling();
  }, [enabled, disclosureReady]);

  return token;
};

export default useJornayaScript;
